import Add from "@mui/icons-material/Add";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";
import React, { useEffect, useRef, useState } from "react";
import { useAppId } from "../../../hook";
import { useMenu } from "../../../hook/menu";
import {
  newNotebookPage,
  useAddNotebookPageAsync,
  useSelectedNotebookPage,
  useSelectedNotebookPageIdMap,
} from "../../../hook/page";
import {
  useFolderPropertyTypes,
  useGetProperty,
} from "../../../hook/page/property";
import {
  MoveDirection,
  useHandleViewTab,
  useSelectedDatabaseView,
} from "../../../hook/page/view";
import { useDatabaseTableSearch } from "../../../hook/table/database";
import { NotebookPage } from "../../../types/note";
import { getViewPropId } from "../../../types/notebook/view";
import { DynamicTextField } from "../../dynamicTextField";
import { EditPageMenu } from "../../menu/page";
import { NoteBlockContainer } from "../../note";
import { NotebookDatabaseToolBar, NotebookPageNoteTextFieldElement } from "../view";
import { PageLink } from "../pageLink";
import { NotePropertyValue } from "../property";
import { AddPropertyMenu, PropertyEditMenu } from "../../menu/page/property";

export const PropertyTypeHeaderCell: React.FC<{
  propName: string;
}> = ({ propName }) => {
  const { anchorEl, handleOpen, onClose, open } = useMenu();

  return (
    <TableCell onContextMenu={handleOpen}>
      <PropertyEditMenu
        propName={propName}
        anchorEl={anchorEl}
        onClose={onClose}
        open={open}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
      <Typography>{propName}</Typography>
    </TableCell>
  );
};

export const PropertyValueCell: React.FC<
  {
    pageId: string;
    folderId: string;
    propName: string;
  } & TableCellProps
> = ({ pageId, folderId, propName, ...tableCellProps }) => {
  return (
    <TableCell {...tableCellProps}>
      <NotePropertyValue
        propName={propName}
        pageId={pageId}
        folderId={folderId}
      />
    </TableCell>
  );
};

export const PageLinkCell: React.FC<
  {
    pageId: string;
  } & TableCellProps
> = ({ pageId, ...tableCellProps }) => (
  <TableCell {...tableCellProps}>
    <PageLink pageId={pageId} data-testid={`page-link-${pageId}`} />
  </TableCell>
);

export const PageNameCell: React.FC<
  {
    pageId: string;
  } & TableCellProps
> = ({ pageId, ...tableCellProps }) => {
  const id = getViewPropId({ pageId, propId: "page-name" });

  return (
    <TableCell {...tableCellProps}>
      <NotebookPageNoteTextFieldElement pageId={pageId} id={id} />
    </TableCell>
  );
};

export const CreatePageCell: React.FC<
  {
    folderId: string;
    addPageCallBack?: (page: NotebookPage) => void;
  } & TableCellProps
> = ({ folderId, addPageCallBack, ...tableCellProps }) => {
  const [appId] = useAppId();
  const addPageAsync = useAddNotebookPageAsync();
  const id = getViewPropId({ pageId: "add-page", propId: "add-page" });

  return (
    <TableCell {...tableCellProps}>
      <DynamicTextField
        placeholder="Page Name"
        text=""
        id={id}
        setText={(v) => {
          const page = newNotebookPage({ folderId, appId: appId!, name: v });
          addPageAsync(page);
          addPageCallBack && addPageCallBack(page);
          setTimeout(() => {
            const viewId = getViewPropId({
              pageId: page["page-id"],
              propId: "page-name",
            });
            const elem = document.getElementById(viewId);
            elem?.dispatchEvent(new CustomEvent("onEdit", { detail: {} }));
          }, 0);
        }}
      />
    </TableCell>
  );
};

export const DatabaseTableView: React.FC<{}> = () => {
  const page = useSelectedNotebookPage();
  const folderId = page!["page-id"];
  const view = useSelectedDatabaseView();
  const viewName = view?.name ?? "";
  const { folderProps } = useFolderPropertyTypes(folderId);
  const { isLoading, index, setIndex, tableCount, pages, pageSize, addPage } =
    useDatabaseTableSearch(folderId);
  const {
    anchorEl,
    open,
    onClose: handleClose,
    handleOpen,
    setAnchorEl,
  } = useMenu();
  const {
    handleOpen: editPageMenuHandleOpen,
    onClose: editPageMenuOnClose,
    open: editPageMenuOpen,
    anchorEl: editPageMenuAnchorEl,
    setAnchorEl: editPageMenuSetAnchorEl,
  } = useMenu();
  const cellWidth = "15rem";
  const [selectedPageIdMenu, setSelectedPageIdMenu] = useState<
    string | undefined
  >(undefined);

  return (
    <>
      <EditPageMenu
        anchorEl={editPageMenuAnchorEl}
        onClose={editPageMenuOnClose}
        open={editPageMenuOpen}
      />
      <AddPropertyMenu anchorEl={anchorEl} onClose={handleClose} open={open} />
      <NoteBlockContainer toolbar={<NotebookDatabaseToolBar />} name={viewName}>
        {isLoading ? (
          <CasLoader />
        ) : (
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table key={folderId}>
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell>Name</TableCell>
                  {Object.values(folderProps ?? []).map((prop, i) => {
                    return (
                      <PropertyTypeHeaderCell key={i} propName={prop["name"]} />
                    );
                  })}
                  <TableCell
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <IconButton color="primary" onClick={handleOpen} data-testid="add-column-btn">
                      <Add />
                    </IconButton>
                    <Typography color="text.secondary">Add Property</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pages.map((page, rowIndex) => {
                  const pageId = page["page-id"];
                  const folderId = page["folder-id"];

                  return (
                    <TableRow key={pageId}>
                      <PageLinkCell
                        pageId={pageId}
                        sx={{ maxWidth: cellWidth, minWidth: cellWidth }}
                        onContextMenu={(e) => {
                          editPageMenuHandleOpen(e);
                          setSelectedPageIdMenu(pageId);
                        }}
                      />
                      <PageNameCell
                        pageId={pageId}
                        sx={{ maxWidth: cellWidth, minWidth: cellWidth }}
                        onContextMenu={(e) => {
                          editPageMenuHandleOpen(e);
                          setSelectedPageIdMenu(pageId);
                        }}
                      />
                      {Object.values(folderProps ?? {}).map((prop, i) => {
                        const propName = prop.name;
                        return (
                          <PropertyValueCell
                            key={i}
                            pageId={pageId}
                            propName={propName}
                            folderId={folderId}
                            sx={{ maxWidth: cellWidth, minWidth: cellWidth }}
                            onContextMenu={editPageMenuHandleOpen}
                          />
                        );
                      })}
                    </TableRow>
                  );
                })}
                <TableRow>
                  <CreatePageCell
                    folderId={folderId}
                    addPageCallBack={addPage}
                  />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </NoteBlockContainer>
      <TablePagination
        component="div"
        onPageChange={(e, value) => setIndex(value)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        page={index}
        count={tableCount}
        showFirstButton={true}
        showLastButton={true}
      />
    </>
  );
};
